import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListGlobalRules,
  useCreateGlobalRule,
  useUpdateGlobalRule,
  useDeleteGlobalRule,
  getListGlobalRulesQueryKey,
  type GlobalRule,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, Settings as SettingsIcon } from "lucide-react";
import { GoogleConnectionsPanel } from "@/components/google-connections";
import { AsistanAyarlarPanel } from "@/components/asistan-ayarlar-panel";
import { WpBaglantiPanel } from "@/components/wp-baglanti-panel";
import { ErisimPanel } from "@/components/erisim-panel";

const SCOPE_LABELS: Record<GlobalRule["scope"], string> = {
  all: "Tüm Modüller",
  clustering: "Cluster Oluşturma",
  link_suggestions: "Link Önerileri",
  keyword_extraction: "Anahtar Kelime Çıkarımı",
  content: "İçerik (yakında)",
  technical_seo: "Teknik SEO (yakında)",
  serp_tracking: "SERP Takibi (yakında)",
  content_brief: "İçerik Brief (yakında)",
};

const KIND_LABELS: Record<GlobalRule["kind"], string> = {
  must: "ZORUNLU",
  must_not: "YASAK",
  guideline: "ÖNERİ",
};

const KIND_VARIANT: Record<
  GlobalRule["kind"],
  "default" | "destructive" | "secondary" | "outline"
> = {
  must: "default",
  must_not: "destructive",
  guideline: "secondary",
};

const SCOPE_OPTIONS = Object.keys(SCOPE_LABELS) as GlobalRule["scope"][];

interface RuleFormState {
  scope: GlobalRule["scope"];
  category: string;
  title: string;
  body: string;
  kind: GlobalRule["kind"];
  enabled: boolean;
  priority: number;
}

const EMPTY_FORM: RuleFormState = {
  scope: "all",
  category: "",
  title: "",
  body: "",
  kind: "guideline",
  enabled: true,
  priority: 0,
};

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: rules, isLoading } = useListGlobalRules();
  const [filterScope, setFilterScope] = useState<"__all__" | GlobalRule["scope"]>("__all__");
  const [editing, setEditing] = useState<GlobalRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListGlobalRulesQueryKey() });

  const createMut = useCreateGlobalRule({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast({ title: "Kural eklendi" });
        setCreating(false);
        setForm(EMPTY_FORM);
      },
      onError: () => toast({ title: "Kural eklenemedi", variant: "destructive" }),
    },
  });
  const updateMut = useUpdateGlobalRule({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast({ title: "Kural güncellendi" });
        setEditing(null);
      },
      onError: () => toast({ title: "Güncelleme başarısız", variant: "destructive" }),
    },
  });
  const deleteMut = useDeleteGlobalRule({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast({ title: "Kural silindi" });
        setDeleteId(null);
      },
      onError: () => toast({ title: "Silme başarısız", variant: "destructive" }),
    },
  });

  const filteredRules = useMemo(() => {
    if (!rules) return [];
    if (filterScope === "__all__") return rules;
    return rules.filter((r) => r.scope === filterScope);
  }, [rules, filterScope]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreating(true);
  };

  const openEdit = (rule: GlobalRule) => {
    setEditing(rule);
    setForm({
      scope: rule.scope,
      category: rule.category ?? "",
      title: rule.title,
      body: rule.body,
      kind: rule.kind,
      enabled: rule.enabled,
      priority: rule.priority,
    });
  };

  const submitCreate = () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast({ title: "Başlık ve kural metni zorunlu", variant: "destructive" });
      return;
    }
    createMut.mutate({
      data: {
        scope: form.scope,
        category: form.category.trim() || null,
        title: form.title.trim(),
        body: form.body.trim(),
        kind: form.kind,
        enabled: form.enabled,
        priority: form.priority,
      },
    });
  };

  const submitUpdate = () => {
    if (!editing) return;
    if (!form.title.trim() || !form.body.trim()) {
      toast({ title: "Başlık ve kural metni zorunlu", variant: "destructive" });
      return;
    }
    updateMut.mutate({
      ruleId: editing.id,
      data: {
        scope: form.scope,
        category: form.category.trim() || null,
        title: form.title.trim(),
        body: form.body.trim(),
        kind: form.kind,
        enabled: form.enabled,
        priority: form.priority,
      },
    });
  };

  const toggleEnabled = (rule: GlobalRule, enabled: boolean) => {
    updateMut.mutate({ ruleId: rule.id, data: { enabled } });
  };

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-6 h-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold">Ayarlar</h1>
          <p className="text-sm text-muted-foreground">
            Tüm modüllerin uyacağı evrensel kuralları buradan yönetin.
          </p>
        </div>
      </div>

      <Tabs defaultValue="rules" className="w-full">
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-rules">
            Genel Kurallar
          </TabsTrigger>
          <TabsTrigger value="google" data-testid="tab-google">
            Google Bağlantısı
          </TabsTrigger>
          <TabsTrigger value="asistan" data-testid="tab-asistan">
            Geolop Asistanı
          </TabsTrigger>
          <TabsTrigger value="wordpress" data-testid="tab-wordpress">
            WordPress
          </TabsTrigger>
          <TabsTrigger value="erisim" data-testid="tab-erisim">
            Müşteri Erişimi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google" className="space-y-4">
          <GoogleConnectionsPanel />
        </TabsContent>

        <TabsContent value="asistan" className="space-y-4">
          <AsistanAyarlarPanel />
        </TabsContent>

        <TabsContent value="wordpress" className="space-y-4">
          <WpBaglantiPanel />
        </TabsContent>

        <TabsContent value="erisim" className="space-y-4">
          <ErisimPanel />
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Label className="text-sm whitespace-nowrap">Modüle göre filtre:</Label>
                <Select
                  value={filterScope}
                  onValueChange={(v) => setFilterScope(v as typeof filterScope)}
                >
                  <SelectTrigger className="w-64" data-testid="select-filter-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Tümü</SelectItem>
                    {SCOPE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {SCOPE_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={openCreate} data-testid="button-new-rule">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Kural Ekle
              </Button>
            </div>
          </Card>

          {isLoading ? (
            <Card className="p-8 text-center text-muted-foreground">Yükleniyor…</Card>
          ) : filteredRules.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">
              Bu filtreye uyan kural bulunmuyor. "Yeni Kural Ekle" ile başlayın.
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredRules.map((rule) => (
                <Card key={rule.id} className="p-4" data-testid={`rule-${rule.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant={KIND_VARIANT[rule.kind]}>
                          {KIND_LABELS[rule.kind]}
                        </Badge>
                        <Badge variant="outline">{SCOPE_LABELS[rule.scope]}</Badge>
                        {rule.category && (
                          <Badge variant="outline" className="text-xs">
                            {rule.category}
                          </Badge>
                        )}
                        {!rule.enabled && (
                          <Badge variant="secondary" className="text-xs">
                            Pasif
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Öncelik: {rule.priority}
                        </span>
                      </div>
                      <h3 className="font-semibold mb-1">{rule.title}</h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {rule.body}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={(v) => toggleEnabled(rule, v)}
                        data-testid={`switch-enabled-${rule.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(rule)}
                        data-testid={`button-edit-${rule.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(rule.id)}
                        data-testid={`button-delete-${rule.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Kuralı Düzenle" : "Yeni Kural Ekle"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kapsam (Scope)</Label>
                <Select
                  value={form.scope}
                  onValueChange={(v) =>
                    setForm({ ...form, scope: v as GlobalRule["scope"] })
                  }
                >
                  <SelectTrigger data-testid="select-form-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {SCOPE_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tür</Label>
                <Select
                  value={form.kind}
                  onValueChange={(v) =>
                    setForm({ ...form, kind: v as GlobalRule["kind"] })
                  }
                >
                  <SelectTrigger data-testid="select-form-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="must">Zorunlu (uyulmak zorunda)</SelectItem>
                    <SelectItem value="must_not">Yasak (asla yapma)</SelectItem>
                    <SelectItem value="guideline">Öneri (rehber)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Başlık</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Örn: Marka adını anahtar kelimeye dahil etme"
                data-testid="input-form-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Kural Metni</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Yapay zekaya gönderilecek tam talimat metni…"
                rows={5}
                data-testid="input-form-body"
              />
              <p className="text-xs text-muted-foreground">
                Bu metin, ilgili modülün AI prompt'una "EVRENSEL KURALLAR" başlığı altında eklenecek.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori (opsiyonel)</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Örn: ton, dil, anchor"
                />
              </div>
              <div className="space-y-2">
                <Label>Öncelik</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              />
              <Label>Aktif</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
            >
              İptal
            </Button>
            <Button
              onClick={editing ? submitUpdate : submitCreate}
              disabled={createMut.isPending || updateMut.isPending}
              data-testid="button-save-rule"
            >
              {editing ? "Kaydet" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteId !== null}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kuralı sil?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu kural kalıcı olarak silinecek ve ilgili modüllerin AI prompt'larından çıkarılacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMut.mutate({ ruleId: deleteId })}
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
